import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { AuthModal } from "./AuthModal";
import { CatalogSearch } from "./CatalogSearch";

export function SiteLayout() {
  const { user, loading, isAdmin, signOut } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isVipPage = location.pathname === "/vip";
  const closeAuth = useCallback(() => setAuthOpen(false), []);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("entrar") === "1") setAuthOpen(true);
    setAccountOpen(false);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const displayName = String(user?.user_metadata?.display_name || user?.email?.split("@")[0] || "Conta");
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();

  return (
    <div className={`site-shell ${isVipPage ? "vip-site-shell" : ""}`}>
      {!isVipPage && <header className="site-header">
        <div className="header-inner">
          <Link className="brand" to="/" aria-label="DoramaFlix — página inicial">
            DORAMAFLIX
          </Link>
          <nav aria-label="Navegação principal">
            <NavLink to="/">Início</NavLink>
            <a href="/#romance">Romance</a>
            <a href="/#action">Ação</a>
            <a href="/#drama">Drama</a>
          </nav>
          <div className="account-actions">
            <CatalogSearch />
            <Link className="header-icon-button purchases-shortcut" to="/minhas-compras" aria-label="Minhas compras">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </Link>
            {!loading && user ? (
              <div className="account-menu-wrap" ref={accountRef}>
                <button className="account-avatar" type="button" onClick={() => setAccountOpen((current) => !current)} aria-expanded={accountOpen} aria-label="Abrir menu da conta">{initials || "DF"}</button>
                {accountOpen && <div className="account-menu" role="menu">
                  <div className="account-menu-profile"><span>{initials || "DF"}</span><div><strong>{displayName}</strong><small>{user.email}</small></div></div>
                  <Link to="/minhas-compras" role="menuitem">▱ Minhas compras</Link>
                  {isAdmin && <Link to="/admin" role="menuitem">⚙ Administração</Link>}
                  <button type="button" role="menuitem" onClick={() => { setAccountOpen(false); void signOut(); }}>↪ Sair</button>
                </div>}
              </div>
            ) : (
              <button className="login-button" type="button" onClick={() => setAuthOpen(true)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21a8 8 0 0 0-16 0"/><circle cx="12" cy="7" r="4"/></svg> Entrar</button>
            )}
            <button className="mobile-menu-trigger" type="button" aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"} aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => setMobileOpen((current) => !current)}>
              {mobileOpen ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/></svg>}
            </button>
          </div>
        </div>
      </header>}
      {!isVipPage && mobileOpen && <>
        <button className="mobile-menu-backdrop" type="button" aria-label="Fechar menu" onClick={() => setMobileOpen(false)} />
        <aside className="mobile-menu" id="mobile-menu" aria-label="Menu principal">
          <div className="mobile-menu-heading"><strong>Menu</strong><button type="button" onClick={() => setMobileOpen(false)} aria-label="Fechar menu">×</button></div>
          <nav>
            <NavLink to="/">Início <span>›</span></NavLink>
            <a href="/#romance">Romance <span>›</span></a>
            <a href="/#action">Ação <span>›</span></a>
            <a href="/#drama">Drama <span>›</span></a>
            <NavLink to="/vip">Passe Vitalício <span>›</span></NavLink>
          </nav>
          <a className="mobile-request-link" href="https://t.me/suporte_doramaflix" target="_blank" rel="noreferrer"><span>✈</span><strong>Pedir novo Dorama<small>Fale com a gente pelo Telegram</small></strong></a>
          <div className="mobile-account-section">
            {user ? <>
              <div className="mobile-profile"><span>{initials || "DF"}</span><div><strong>{displayName}</strong><small>{user.email}</small></div></div>
              <Link to="/minhas-compras">▱ Minhas compras</Link>
              {isAdmin && <Link to="/admin">⚙ Administração</Link>}
              <button type="button" onClick={() => { setMobileOpen(false); void signOut(); }}>↪ Sair da conta</button>
            </> : <button className="mobile-login" type="button" onClick={() => { setMobileOpen(false); setAuthOpen(true); }}>Entrar ou criar conta</button>}
          </div>
        </aside>
      </>}
      <main id="conteudo">
        <Outlet />
      </main>
      {!isVipPage && <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <Link to="/" aria-label="DoramaFlix — página inicial"><img src="/assets/doramaflix-footer-logo-CR9x5S0k.png" alt="DoramaFlix" /></Link>
              <p>Sua plataforma de doramas favorita</p>
            </div>
            <nav className="footer-primary-links" aria-label="Links institucionais">
              <Link to="/sobre">Sobre</Link>
              <a href="https://t.me/suporte_doramaflix" target="_blank" rel="noreferrer">Contato</a>
              <Link to="/termos">Termos de Uso</Link>
              <Link to="/privacidade">Privacidade</Link>
            </nav>
          </div>
          <div className="footer-trust" aria-label="Ambiente seguro">
            <p>COMPRA 100% SEGURA</p>
            <img src="/assets/trust-badges-B9tx3S21.png" alt="Selos Norton Secured, Google Site Seguro, SSL Compra Segura e garantia de satisfação" loading="lazy" />
          </div>
          <nav className="footer-guide-links" aria-label="Guias e informações">
            <Link to="/sobre">Sobre o DoramaFlix Oficial</Link>
            <Link to="/guias">Guias</Link>
            <Link to="/guias/onde-assistir-doramas-dublados">Onde assistir doramas dublados</Link>
            <Link to="/guias/como-assistir-doramas-online-sem-assinatura">Assistir sem assinatura</Link>
            <Link to="/vip">Passe Vitalício</Link>
          </nav>
          <p className="footer-copyright">© 2024 DoramaFlix. Todos os direitos reservados.</p>
        </div>
      </footer>}
      {!isVipPage && <a className="telegram-float" href="https://t.me/suporte_doramaflix" target="_blank" rel="noreferrer" aria-label="Falar com o suporte no Telegram">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21.4 3.2 18.2 19c-.2 1.1-.9 1.4-1.8.9l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.4-5 9.1-8.2c.4-.4-.1-.6-.6-.3L5.7 12.7.8 11.2c-1.1-.3-1.1-1.1.2-1.6L20.1 2.2c.9-.3 1.7.2 1.3 1Z"/></svg>
      </a>}
      <AuthModal open={authOpen} onClose={closeAuth} />
    </div>
  );
}
